/**
 * B1.10 DayCloseWizard — mock↔live API dispatcher.
 *
 * Runtime switch driven by {@link APP_ENV.bizApiMode}:
 *   - `mock` (default): serves deterministic seed rows from
 *     {@link ./day-close-api-mock} (Lovable / Storybook / pre-wire sessions).
 *   - `live`: routes through {@link ./day-close-api-http} → `bizClient` →
 *     `/api/biz/cashier/day-close/*` on the backend (Phase E1).
 *
 * The dispatcher is resolved at module load so React Query cache keys,
 * hook signatures, and the Lovable-frozen `DayCloseSnapshot` shape stay
 * identical regardless of the mode. Flipping `VITE_BIZ_API_MODE` (or
 * `import.meta.env.VITE_BIZ_API_MODE` at build time) is enough to graduate
 * the feature from fixtures to real endpoints — no hook rewrites, no
 * downstream churn.
 *
 * Parallels the `cashier-api.ts` and `invoice-api.ts` dispatchers shipped in
 * Phase C2 / Phase D FE rewires.
 *
 * @see day-close-api-mock.ts
 * @see day-close-api-http.ts
 * @see docs/_context/PETOTA_BUSINESS_IMPLEMENTATION_PLAN.md §Phase E FE rewire
 */

import { APP_ENV } from '@/shared/config/env';

import * as mockApi from './day-close-api-mock';
import * as httpApi from './day-close-api-http';

import type {
  DayCloseSnapshot,
  PatchCashCountsRequest,
  PatchDepositRequest,
  PatchNonCashCountsRequest,
  ReopenDayCloseRequest,
  StartDayCloseRequest,
  SubmitDayCloseRequest,
} from './types';

const useLive = APP_ENV.bizApiMode === 'live';

export async function startDayClose(
  req: StartDayCloseRequest,
): Promise<DayCloseSnapshot> {
  return useLive ? httpApi.startDayClose(req) : mockApi.startDayClose(req);
}

export async function getDayCloseSnapshot(
  sessionId: string,
): Promise<DayCloseSnapshot> {
  return useLive
    ? httpApi.getDayCloseSnapshot(sessionId)
    : mockApi.getDayCloseSnapshot(sessionId);
}

export async function patchCashCounts(
  req: PatchCashCountsRequest,
): Promise<DayCloseSnapshot> {
  return useLive ? httpApi.patchCashCounts(req) : mockApi.patchCashCounts(req);
}

export async function patchNonCashCounts(
  req: PatchNonCashCountsRequest,
): Promise<DayCloseSnapshot> {
  return useLive
    ? httpApi.patchNonCashCounts(req)
    : mockApi.patchNonCashCounts(req);
}

export async function patchDeposit(
  req: PatchDepositRequest,
): Promise<DayCloseSnapshot> {
  return useLive ? httpApi.patchDeposit(req) : mockApi.patchDeposit(req);
}

export async function submitDayClose(
  req: SubmitDayCloseRequest,
): Promise<DayCloseSnapshot> {
  return useLive ? httpApi.submitDayClose(req) : mockApi.submitDayClose(req);
}

export async function reopenDayClose(
  req: ReopenDayCloseRequest,
): Promise<DayCloseSnapshot> {
  return useLive ? httpApi.reopenDayClose(req) : mockApi.reopenDayClose(req);
}

/**
 * Live-only resume helper. Returns the in-flight session for a branch when
 * the wizard is re-opened mid-flow (state ∈ {draft, counting, reconciling,
 * review, submitted}). Returns `null` when no active session exists.
 *
 * Only available in live mode; the mock keeps a module-local store, so a
 * session-less "active" probe is undefined for fixtures. Pages that need
 * resume support should gate calls on `getDayCloseApiMode() === 'live'`.
 */
export async function getActiveDayClose(
  branchId: string,
): Promise<DayCloseSnapshot | null> {
  if (!useLive) return null;
  return httpApi.getActiveDayClose(branchId);
}

/**
 * Helper: generate idempotency-key per ADR-022 / ADR-110 contract.
 * Format: `branchId:businessDate:attemptNo`.
 *
 * Mode-agnostic — the format is identical for mock and live, so we re-export
 * the mock implementation directly.
 */
export const buildIdempotencyKey = mockApi.buildIdempotencyKey;

/**
 * Introspection helper — exposes the currently active mode so debug panels
 * and QA tooling can surface the dispatcher choice without re-reading env.
 */
export function getDayCloseApiMode(): 'mock' | 'live' {
  return useLive ? 'live' : 'mock';
}
